import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  ArrowBack,
  Send,
  Download,
  Edit
} from '@mui/icons-material';
import { brand } from '../../theme/colors';

const QuoteRequestConfirmation = ({ project, requestData, onSend, onBack, onCancel, loading, error }) => {
  const selectedMerchantsCount = requestData?.selectedMerchantIds?.length || 0;

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Review & Confirm Quote Request
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBack && (
            <Button size="small" onClick={onBack} startIcon={<ArrowBack />}>
              Back
            </Button>
          )}
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => {}}
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          }
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            Error: {error}
          </Typography>
          <Typography variant="body2">
            Please check the browser console for more details.
          </Typography>
        </Alert>
      )}

      {/* What Happens Next */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          What Happens Next?
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="1️⃣ Suppliers Review Your Request (Next few hours)"
              secondary="Selected suppliers are being notified right now"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="2️⃣ Suppliers Prepare Detailed Quotes (24-72 hours)"
              secondary="You'll typically receive 2-5 quotes within 48 hours"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="3️⃣ You'll Be Notified When Quotes Arrive"
              secondary="Via email, SMS, and in-app notifications"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="4️⃣ Compare Quotes & Place Your Order"
              secondary="Review prices, select items, and order from one or multiple suppliers"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Quote Request Summary */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Quote Request Summary
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Project:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {project?.title || 'N/A'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Materials Requested:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {requestData?.materialCategories?.join(', ') || 'Various'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Delivery Location:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {project?.location || 'TBD'}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Delivery Timeframe:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {requestData?.deliveryTimeframe === 'specific' 
              ? requestData?.deliveryDate 
              : requestData?.deliveryTimeframe || 'TBD'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Suppliers Receiving Request:</Typography>
          <Chip
            label={`${selectedMerchantsCount} supplier${selectedMerchantsCount !== 1 ? 's' : ''}`}
            color="primary"
            sx={{ mt: 1 }}
          />
        </Box>

        {requestData?.specialRequirements && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Special Requirements:</Typography>
            <Typography variant="body2">
              {requestData.specialRequirements}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() => alert('Download coming soon')}
          >
            Download Request Details (PDF)
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={onBack}
          >
            Edit Request
          </Button>
        </Box>
      </Paper>

      {/* Notification Preferences */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f7fa' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Where to Find Your Quotes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          📧 Email: We'll email you when quotes arrive
        </Typography>
        <Typography variant="body2" color="text.secondary">
          📱 SMS: Text alerts (if enabled)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          🔔 Dashboard: Check "Supplier Quotes" section in your project
        </Typography>
        <Button size="small" sx={{ mt: 1 }}>
          Update Notification Preferences
        </Button>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={onCancel || onBack} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Send Quote Requests button clicked');
            console.log('onSend function:', onSend);
            console.log('loading:', loading);
            console.log('selectedMerchantsCount:', selectedMerchantsCount);
            if (onSend) {
              onSend();
            } else {
              console.error('onSend is not defined!');
            }
          }}
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          disabled={loading || selectedMerchantsCount === 0}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            },
            '&:disabled': {
              background: 'grey.300'
            }
          }}
        >
          {loading ? 'Sending...' : 'Send Quote Requests'}
        </Button>
      </Box>
    </Box>
  );
};

export default QuoteRequestConfirmation;

